import axios from 'axios';

// src/api/client.ts

// src/constants/api.ts
var API_DEFAULTS = {
  TIMEOUT_SIMPLE_GET: 5e3};

// src/api/client.ts
function createApiClient(config) {
  const instance = axios.create({
    baseURL: config.baseURL,
    timeout: API_DEFAULTS.TIMEOUT_SIMPLE_GET,
    headers: {
      "Content-Type": "application/json"
    }
  });
  instance.interceptors.request.use((req) => {
    const token = config.getAccessToken();
    if (token && req.headers) {
      req.headers.Authorization = `Bearer ${token}`;
    }
    return req;
  });
  let isRefreshing = false;
  let refreshQueue = [];
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      if (error.response?.status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            refreshQueue.push({
              resolve: (token) => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                resolve(instance(originalRequest));
              },
              reject
            });
          });
        }
        originalRequest._retry = true;
        isRefreshing = true;
        try {
          const newToken = await config.onTokenExpired();
          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            refreshQueue.forEach((q) => q.resolve(newToken));
            refreshQueue = [];
            return instance(originalRequest);
          }
          refreshQueue.forEach((q) => q.reject(error));
          refreshQueue = [];
          config.onAuthFailed();
          return Promise.reject(error);
        } catch (refreshError) {
          refreshQueue.forEach((q) => q.reject(refreshError));
          refreshQueue = [];
          config.onAuthFailed();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }
      return Promise.reject(error);
    }
  );
  return {
    instance,
    get: (url, cfg) => instance.get(url, cfg).then((r) => r.data),
    post: (url, data, cfg) => instance.post(url, data, cfg).then((r) => r.data),
    put: (url, data, cfg) => instance.put(url, data, cfg).then((r) => r.data),
    delete: (url, cfg) => instance.delete(url, cfg).then((r) => r.data),
    patch: (url, data, cfg) => instance.patch(url, data, cfg).then((r) => r.data)
  };
}

// src/api/clearvoxApi.ts
var BASE = "/api/admin/clearvox";
function createClearvoxApi(client) {
  return {
    async getCredentials(locationId) {
      const envelope = await client.get(
        `${BASE}/credentials/${locationId}`
      );
      if (!envelope?.success) {
        throw new Error(envelope?.error || "Failed to fetch Clearvox credentials");
      }
      return envelope.data;
    },
    async updateCredentials(locationId, body) {
      const payload = {
        baseUrl: body.baseUrl,
        user: body.user
      };
      if (typeof body.masterApiKey === "string" && body.masterApiKey.length > 0) {
        payload.masterApiKey = body.masterApiKey;
      }
      if (typeof body.siteUserApiKey === "string" && body.siteUserApiKey.length > 0) {
        payload.siteUserApiKey = body.siteUserApiKey;
      }
      if (typeof body.apiKey === "string" && body.apiKey.length > 0) {
        payload.apiKey = body.apiKey;
      }
      const envelope = await client.put(
        `${BASE}/credentials/${locationId}`,
        payload
      );
      if (!envelope?.success) {
        throw new Error(envelope?.error || "Failed to update Clearvox credentials");
      }
      return envelope.data;
    },
    async deleteCredentials(locationId) {
      const envelope = await client.delete(
        `${BASE}/credentials/${locationId}`
      );
      if (!envelope?.success) {
        throw new Error(envelope?.error || "Failed to delete Clearvox credentials");
      }
      return envelope.data;
    },
    async getPresenceForSite(locationId) {
      const envelope = await client.get(
        `${BASE}/presence/${locationId}`
      );
      if (!envelope?.success) {
        throw new Error(envelope?.error || "Failed to fetch Clearvox presence");
      }
      return envelope.data || [];
    },
    async listUsersForSite(siteLocationId) {
      const envelope = await client.get(
        `${BASE}/users?siteLocationId=${siteLocationId}`
      );
      if (!envelope?.success) {
        throw new Error(envelope?.error || "Failed to fetch Clearvox users");
      }
      return {
        users: envelope.data?.users || [],
        availableUnits: envelope.data?.availableUnits || []
      };
    },
    async assignUserLocation(clearvoxUserId, locationId) {
      const envelope = await client.put(
        `${BASE}/users/${clearvoxUserId}/location`,
        { locationId }
      );
      if (!envelope?.success) {
        throw new Error(envelope?.error || "Failed to assign location");
      }
      return envelope.data.user;
    },
    async suggestUserLocationMatches(siteLocationId) {
      const envelope = await client.get(
        `${BASE}/users/suggestions?siteLocationId=${siteLocationId}`
      );
      if (!envelope?.success) {
        throw new Error(envelope?.error || "Failed to fetch match suggestions");
      }
      return envelope.data?.suggestions || [];
    }
  };
}

// src/api/contactsApi.ts
var CONTACTS_PATH = "/api/residentApp/user/contacts";
async function getResidentContacts(client) {
  const res = await client.get(CONTACTS_PATH);
  return Array.isArray(res?.data?.contacts) ? res.data.contacts : [];
}

export { createApiClient, createClearvoxApi, getResidentContacts };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map